import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import PageMeta from '../../components/common/PageMeta';
import { relativeTime } from '../../shared';
import {
  Callout,
  type DropFile,
  Field,
  IconCheckCircle,
  PasswordInput,
  PublicActions,
  PublicPage,
  PublicPrimary,
  PublicSecondary,
  PublicShell,
  PublicTitle,
  Spinner,
  StepFooter,
  StepNav,
  Stepper,
  SUPPORT_EMAIL,
} from '../../ui/v2';
import { apiEndpoints, apiPublic } from '../../utils/apiService';
import {
  validateUploadFiles,
  validateUploadFilesBasic,
} from '../../utils/fileUploadValidation';
import { validateOnboardingRouting } from '../../utils/onboardingRoutingValidation';
import { normalizeOnboardingPayloadUrls } from '../../utils/urlInput';
import {
  type ActivationPrefill,
  applyPrefill,
  errorCount,
  type FieldErrors,
  firstInvalidStep,
  hasActivationPrefill,
  type IntakeDraft,
  payloadForSubmit,
  type StepKey,
  stepsFor,
  validateStep,
} from './onboarding/model';
import {
  EventsStep,
  FaqsStep,
  OrdersStep,
  PeopleStep,
  ReservationsStep,
  RestaurantStep,
  ReviewStep,
  ServicesStep,
} from './onboarding/steps';
import { useIntakeDraft } from './onboarding/useIntakeDraft';

/**
 * Store onboarding — `/store-onboarding`.
 *
 * The public intake a new restaurant fills in after activation: a stepped
 * form in a wide `PublicShell`, step list on the left at ≥lg (`StepNav`),
 * a compact `Stepper` above the form below it. Steps for services the
 * restaurant didn't pick drop out.
 *
 * Access: `?activation=<token>` (from the activation checkout) is checked
 * with the prefill call; an unknown token gets one invalid-link screen.
 * Without a token the shared access password still applies until the
 * activation link replaces it (PUBLIC_V2_SPEC P1).
 *
 * The draft saves on this device as you type; validation runs per step on
 * Continue, errors sit under their field and the first one gets focus.
 */
const FORM_PASSWORD = 'Voice2026';
const UNLOCK_KEY = 'storeOnboardingUnlocked';

const isUnlocked = () => {
  try {
    return sessionStorage.getItem(UNLOCK_KEY) === 'true';
  } catch {
    return false;
  }
};

const unlock = () => {
  try {
    sessionStorage.setItem(UNLOCK_KEY, 'true');
  } catch {
    /* storage unavailable: unlocked for this page view only */
  }
};

const StoreOnboardingPage: React.FC = () => {
  const [params] = useSearchParams();
  const token = params.get('activation');
  return token ? (
    <ActivationGate key={token} token={token} />
  ) : (
    <PasswordGate />
  );
};

export default StoreOnboardingPage;

// ---------------------------------------------------------------------------
// Gates
// ---------------------------------------------------------------------------

type GateState =
  | { kind: 'checking' }
  | { kind: 'ok'; prefill: ActivationPrefill | null }
  | { kind: 'invalid' }
  | { kind: 'error' };

const ActivationGate: React.FC<{ token: string }> = ({ token }) => {
  const [state, setState] = useState<GateState>({ kind: 'checking' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'checking' });
    (async () => {
      try {
        const res = await apiPublic.get(
          apiEndpoints.getPublicActivationOfferOnboardingPrefill(token),
        );
        if (cancelled) return;
        if (res.ok) {
          const body = (await res
            .json()
            .catch(() => null)) as ActivationPrefill | null;
          if (cancelled) return;
          unlock();
          setState({
            kind: 'ok',
            prefill: hasActivationPrefill(body) ? body : null,
          });
        } else {
          setState({ kind: res.status === 404 ? 'invalid' : 'error' });
        }
      } catch {
        if (!cancelled) setState({ kind: 'error' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, attempt]);

  if (state.kind === 'ok')
    return <Intake activation={token} prefill={state.prefill} />;

  if (state.kind === 'checking') {
    return (
      <PublicPage>
        <PageMeta
          title="Restaurant onboarding · AskArthur"
          description="Restaurant onboarding"
        />
        <PublicShell width="form">
          <PublicTitle>Opening your onboarding…</PublicTitle>
          <div
            className="mt-6 flex items-center gap-3 text-v2-13 text-v2-fg-2"
            role="status"
            aria-live="polite"
          >
            <Spinner className="h-4 w-4" /> One moment
          </div>
        </PublicShell>
      </PublicPage>
    );
  }

  if (state.kind === 'invalid') {
    return (
      <PublicPage>
        <PageMeta
          title="This link isn’t valid · AskArthur"
          description="Onboarding link"
        />
        <PublicShell width="form">
          <PublicTitle lede="Onboarding links are personal to your activation. Open the most recent email from AskArthur, or ask us for a fresh one.">
            This link isn’t valid
          </PublicTitle>
          <PublicActions className="mt-6">
            <PublicPrimary
              onClick={() => {
                window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('New onboarding link')}`;
              }}
            >
              Ask for a new link
            </PublicPrimary>
          </PublicActions>
        </PublicShell>
      </PublicPage>
    );
  }

  return (
    <PublicPage>
      <PageMeta
        title="Couldn’t open this link · AskArthur"
        description="Onboarding link"
      />
      <PublicShell width="form">
        <PublicTitle lede="We couldn’t check your onboarding link just now. Try again in a moment, or continue without the details from your activation.">
          Couldn’t open this link
        </PublicTitle>
        <PublicActions className="mt-6">
          <PublicSecondary
            onClick={() => {
              unlock();
              setState({ kind: 'ok', prefill: null });
            }}
          >
            Continue anyway
          </PublicSecondary>
          <PublicPrimary onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </PublicPrimary>
        </PublicActions>
      </PublicShell>
    </PublicPage>
  );
};

const PasswordGate: React.FC = () => {
  const [unlocked, setUnlocked] = useState(isUnlocked);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  if (unlocked) return <Intake activation={null} prefill={null} />;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === FORM_PASSWORD) {
      unlock();
      setUnlocked(true);
      return;
    }
    setError(
      password
        ? 'That password isn’t right. Check the email from your AskArthur contact.'
        : 'Enter the access password.',
    );
    inputRef.current?.focus();
  };

  return (
    <PublicPage>
      <PageMeta
        title="Restaurant onboarding · AskArthur"
        description="Restaurant onboarding"
      />
      <PublicShell width="form">
        <PublicTitle lede="Enter the access password your AskArthur contact sent you.">
          Restaurant onboarding
        </PublicTitle>
        <form onSubmit={submit} noValidate className="mt-6 flex flex-col gap-4">
          <Field label="Access password" error={error ?? undefined}>
            {(id, describedBy) => (
              <PasswordInput
                ref={inputRef}
                id={id}
                name="password"
                aria-describedby={describedBy}
                invalid={!!error}
                autoComplete="off"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (error) setError(null);
                }}
              />
            )}
          </Field>
          <PublicActions>
            <PublicPrimary type="submit">Continue</PublicPrimary>
          </PublicActions>
        </form>
        <p className="mt-6 text-v2-12 text-v2-fg-3">
          No password?{' '}
          <a
            href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('Onboarding access')}`}
            className="text-v2-fg-2 underline underline-offset-2"
          >
            Email {SUPPORT_EMAIL}
          </a>
          .
        </p>
      </PublicShell>
    </PublicPage>
  );
};

// ---------------------------------------------------------------------------
// The intake
// ---------------------------------------------------------------------------

const fileError = (file: File, message: string) =>
  message.replace(`${file.name}: `, '');

const submitErrorFrom = (data: unknown): string | null => {
  if (!data || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;
  if (typeof d.detail === 'string' && d.detail) return d.detail;
  if (typeof d.message === 'string' && d.message) return d.message;
  const field = Object.values(d).find(
    (v): v is unknown[] => Array.isArray(v) && v.length > 0,
  );
  return field ? String(field[0]) : null;
};

const Intake: React.FC<{
  activation: string | null;
  prefill: ActivationPrefill | null;
}> = ({ activation, prefill }) => {
  const [addressResolving, setAddressResolving] = useState(false);
  const { draft, update, reset, savedAt, restored } = useIntakeDraft(
    activation ?? 'default',
    addressResolving,
  );
  const [errors, setErrors] = useState<FieldErrors>({});
  const [files, setFiles] = useState<DropFile[]>([]);
  const fileObjects = useRef(new Map<string, File>());
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [done, setDone] = useState<{
    id: number | null;
    draft: IntakeDraft;
  } | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const compactHeadingRef = useRef<HTMLDivElement>(null);
  const prefilledOnce = useRef(false);

  // Fill blanks from the activation once, and only into a fresh draft.
  useEffect(() => {
    if (
      !prefill ||
      !hasActivationPrefill(prefill) ||
      prefilledOnce.current ||
      restored
    )
      return;
    const source = prefill;
    prefilledOnce.current = true;
    update((d) => ({ ...d, payload: applyPrefill(d.payload, source) }));
  }, [prefill, restored, update]);

  const steps = useMemo(() => stepsFor(draft.payload), [draft.payload]);
  const idx = Math.max(
    0,
    steps.findIndex((s) => s.key === draft.step),
  );
  const step = steps[idx];
  const isReview = step.key === 'review';
  const reviewed = draft.reviewed || isReview;

  const later = steps.slice(idx + 1);
  const nextKey = reviewed
    ? later.find(
        (s) =>
          s.key === 'review' ||
          !draft.visited.includes(s.key) ||
          errorCount(validateStep(s.key, draft)) > 0,
      )?.key
    : later[0]?.key;

  const go = useCallback(
    (key: StepKey, markVisited?: StepKey) => {
      if (addressResolving) return;
      setErrors({});
      setSubmitError(null);
      update((d) => ({
        ...d,
        step: key,
        reviewed: d.reviewed || key === 'review' || d.step === 'review',
        visited:
          markVisited && !d.visited.includes(markVisited)
            ? [...d.visited, markVisited]
            : d.visited,
      }));
      window.scrollTo({ top: 0 });
      requestAnimationFrame(() => {
        const wide = window.matchMedia?.('(min-width: 1024px)').matches;
        (wide ? headingRef : compactHeadingRef).current?.focus();
      });
    },
    [addressResolving, update],
  );

  const focusFirstError = (e: FieldErrors) => {
    const first = Object.keys(e).find((k) => e[k]);
    if (!first) return;
    requestAnimationFrame(() => {
      const slot = document.querySelector<HTMLElement>(
        `[data-field="${first}"]`,
      );
      if (!slot) return;
      slot.scrollIntoView?.({ block: 'center' });
      slot
        .querySelector<HTMLElement>('input, select, textarea, button')
        ?.focus();
    });
  };

  const showErrors = (e: FieldErrors) => {
    setErrors(e);
    focusFirstError(e);
  };

  const next = () => {
    if (addressResolving) return;
    const e = validateStep(step.key, draft);
    if (errorCount(e) > 0) return showErrors(e);
    if (nextKey) go(nextKey, step.key);
  };

  const back = () => {
    const before = steps[idx - 1];
    if (before) go(before.key);
  };

  const clearError = useCallback(
    (k: string) => setErrors((e) => (e[k] ? { ...e, [k]: undefined } : e)),
    [],
  );

  const addFiles = (list: File[]) => {
    const added: DropFile[] = list.map((file) => {
      const id = `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2, 7)}`;
      fileObjects.current.set(id, file);
      const basic = validateUploadFilesBasic([file]);
      return {
        id,
        name: file.name,
        size: file.size,
        status: basic ? 'failed' : 'checking',
        error: basic ? fileError(file, basic) : undefined,
      };
    });
    setFiles((f) => [...f, ...added]);
    added
      .filter((a) => a.status === 'checking')
      .forEach(async (a) => {
        const file = fileObjects.current.get(a.id);
        if (!file) return;
        const err = await validateUploadFiles([file]);
        setFiles((f) =>
          f.map((x) =>
            x.id === a.id
              ? {
                  ...x,
                  status: err ? 'failed' : 'ready',
                  error: err ? fileError(file, err) : undefined,
                }
              : x,
          ),
        );
      });
  };

  const removeFile = (id: string) => {
    fileObjects.current.delete(id);
    setFiles((f) => f.filter((x) => x.id !== id));
  };

  const badFiles = files.filter((f) => f.status === 'failed').length;
  const checkingFiles = files.some((f) => f.status === 'checking');

  const submit = async () => {
    // Every step re-validates, so a review reached through Edit can't submit a hole.
    const invalid = firstInvalidStep(draft);
    if (invalid) {
      go(invalid.step);
      showErrors(invalid.errors);
      return;
    }
    const payload = normalizeOnboardingPayloadUrls(payloadForSubmit(draft));
    const routingError = validateOnboardingRouting(payload);
    if (routingError) {
      setSubmitError(routingError);
      return;
    }
    const ready = files
      .filter((f) => f.status === 'ready')
      .map((f) => fileObjects.current.get(f.id))
      .filter((f): f is File => !!f);

    setSubmitting(true);
    setSubmitError(null);
    try {
      let res: Response;
      if (ready.length > 0) {
        const form = new FormData();
        form.append('payload', JSON.stringify(payload));
        if (activation) form.append('activation_token', activation);
        ready.forEach((file) => form.append('files', file));
        res = await apiPublic.postForm(
          apiEndpoints.submitPublicOnboardingIntake(),
          form,
        );
      } else {
        res = await apiPublic.post(
          apiEndpoints.submitPublicOnboardingIntake(),
          {
            payload,
            ...(activation ? { activation_token: activation } : {}),
          },
        );
      }
      const data: unknown = await res.json().catch(() => null);
      if (!res.ok) {
        const reason = submitErrorFrom(data);
        setSubmitError(
          reason
            ? `We couldn’t save your answers: ${reason}`
            : 'We couldn’t save your information right now. Please contact AskArthur support.',
        );
        return;
      }
      const id = (data as { id?: unknown } | null)?.id;
      setDone({ id: typeof id === 'number' ? id : null, draft });
      reset();
      fileObjects.current.clear();
      setFiles([]);
      window.scrollTo({ top: 0 });
    } catch {
      setSubmitError(
        'Can’t reach AskArthur. Your answers are saved on this device — try again in a moment.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (done) return <Success draft={done.draft} id={done.id} />;

  const stepProps = { draft, update, errors, clearError, goToStep: go };
  const note = savedAt ? (
    <>
      Draft saved{' '}
      <span className="v2-num">
        {relativeTime(new Date(savedAt).toISOString())}
      </span>{' '}
      on this device
    </>
  ) : (
    'Your answers save on this device as you type'
  );
  const backToReview = reviewed && nextKey === 'review';

  return (
    <PublicPage>
      <PageMeta
        title={`${step.label} · Restaurant onboarding · AskArthur`}
        description="Restaurant onboarding"
      />
      <PublicShell width="wide" flush>
        <div className="grid lg:grid-cols-[224px_minmax(0,1fr)]">
          <aside className="hidden border-r border-v2-line px-4 py-6 lg:block">
            <div className="sticky top-6">
              <p className="px-2 text-v2-11 font-medium text-v2-fg-4">
                Restaurant onboarding
              </p>
              {draft.payload.business.restaurant_name && (
                <p className="mt-0.5 truncate px-2 text-v2-13 font-semibold text-v2-fg">
                  {draft.payload.business.restaurant_name}
                </p>
              )}
              <StepNav
                steps={steps}
                current={step.key}
                completed={draft.visited}
                onSelect={(k) => go(k)}
                selectDisabledReason={
                  addressResolving ? 'Still checking the address' : undefined
                }
                className="mt-4"
              />
            </div>
          </aside>
          <div className="min-w-0 px-5 py-6 sm:px-7 sm:py-7">
            <div
              ref={compactHeadingRef}
              tabIndex={-1}
              className="focus:outline-none lg:hidden"
            >
              <p className="text-v2-11 font-medium text-v2-fg-4">
                Restaurant onboarding
              </p>
              <Stepper steps={steps} current={step.key} className="mt-2" />
              {step.description && (
                <p className="mt-2 text-v2-12 text-v2-fg-3">
                  {step.description}
                </p>
              )}
            </div>
            <div className="hidden lg:block">
              <p className="v2-num text-v2-12 text-v2-fg-3">
                Step {idx + 1} of {steps.length}
              </p>
              <h2
                ref={headingRef}
                tabIndex={-1}
                className="mt-0.5 text-v2-20 font-semibold tracking-[-0.01em] text-v2-fg focus:outline-none"
              >
                {step.label}
              </h2>
              {step.description && (
                <p className="mt-1 text-v2-13 text-v2-fg-3">
                  {step.description}
                </p>
              )}
            </div>

            <div className="mt-7">
              {step.key === 'restaurant' && (
                <RestaurantStep
                  {...stepProps}
                  prefilled={
                    hasActivationPrefill(prefill) &&
                    !draft.visited.includes('restaurant')
                  }
                  onAddressResolving={setAddressResolving}
                />
              )}
              {step.key === 'people' && <PeopleStep {...stepProps} />}
              {step.key === 'services' && <ServicesStep {...stepProps} />}
              {step.key === 'orders' && <OrdersStep {...stepProps} />}
              {step.key === 'reservations' && (
                <ReservationsStep {...stepProps} />
              )}
              {step.key === 'events' && <EventsStep {...stepProps} />}
              {step.key === 'faqs' && <FaqsStep {...stepProps} />}
              {step.key === 'review' && (
                <ReviewStep
                  {...stepProps}
                  files={files}
                  onAddFiles={addFiles}
                  onRemoveFile={removeFile}
                  onEdit={(k) => go(k)}
                  disabled={submitting}
                />
              )}
            </div>

            {errorCount(errors) > 1 && (
              <Callout tone="danger" className="mt-6">
                {errorCount(errors)} things need a look above before you
                continue.
              </Callout>
            )}
            {submitError && (
              <Callout tone="danger" className="mt-6">
                {submitError}
              </Callout>
            )}
            <StepFooter
              className="mt-8"
              onBack={idx > 0 ? back : undefined}
              onNext={isReview ? submit : next}
              nextLabel={
                isReview
                  ? 'Submit onboarding'
                  : backToReview
                    ? 'Back to review'
                    : idx === steps.length - 2
                      ? 'Review answers'
                      : 'Continue'
              }
              busy={submitting}
              nextDisabledReason={
                isReview
                  ? badFiles > 0
                    ? `Remove the ${badFiles === 1 ? 'file that failed' : `${badFiles} files that failed`} first`
                    : checkingFiles
                      ? 'Still checking a file'
                      : undefined
                  : addressResolving
                    ? 'Still checking the address'
                    : undefined
              }
              note={note}
            />
            {addressResolving && (
              <p className="mt-2 text-v2-12 text-v2-fg-3 sm:text-right">
                Checking the address…
              </p>
            )}
            {isReview && (badFiles > 0 || checkingFiles) && (
              <p className="mt-2 text-v2-12 text-v2-fg-3 sm:text-right">
                {badFiles > 0
                  ? 'Files marked Failed can’t be sent — remove them, or fix and add again.'
                  : 'Checking the file contents…'}
              </p>
            )}
          </div>
        </div>
      </PublicShell>
    </PublicPage>
  );
};

// ---------------------------------------------------------------------------
// Done
// ---------------------------------------------------------------------------

const Success: React.FC<{ draft: IntakeDraft; id: number | null }> = ({
  draft,
  id,
}) => {
  const first = draft.payload.primary_contact.name.trim().split(/\s+/)[0];
  const name = draft.payload.business.restaurant_name || 'your restaurant';
  const email = draft.payload.primary_contact.email;
  return (
    <PublicPage>
      <PageMeta
        title="Thanks — we’ve got it · AskArthur"
        description="Onboarding submitted"
      />
      <PublicShell width="form">
        <div className="flex items-start gap-3">
          <span className="mt-1 text-v2-success" aria-hidden>
            <IconCheckCircle size={22} />
          </span>
          <PublicTitle
            lede={
              <>
                We’ll build Arthur for {name} from these answers and email{' '}
                {email ? (
                  <span className="font-medium text-v2-fg">{email}</span>
                ) : (
                  'you'
                )}{' '}
                within two business days with a test number and a time to walk
                through it.
              </>
            }
          >
            {first
              ? `Thanks, ${first} — we’ve got it`
              : 'Thanks — we’ve got it'}
          </PublicTitle>
        </div>
        {id != null && (
          <p className="v2-num mt-4 text-v2-12 text-v2-fg-3">
            Reference #{id}. Keep it if you write to us about this setup.
          </p>
        )}
        <h2 className="mt-6 text-v2-13 font-semibold">What happens next</h2>
        <ol className="mt-2 flex flex-col gap-1.5 text-v2-13 text-v2-fg-2">
          {[
            'We set Arthur up from your answers and any menus you sent.',
            'You get a test number to call and try before anything changes for guests.',
            'We go live when you say so. Forwarding your line takes about five minutes.',
          ].map((line, i) => (
            <li key={line} className="flex gap-2">
              <span className="v2-num w-4 shrink-0 text-v2-fg-4">{i + 1}</span>
              {line}
            </li>
          ))}
        </ol>
        <PublicActions className="mt-6">
          <PublicSecondary variant="secondary" onClick={() => window.print()}>
            Print a copy
          </PublicSecondary>
        </PublicActions>
      </PublicShell>
    </PublicPage>
  );
};
